import { ExternalLink } from 'lucide-react'
import { useState } from 'react'
import { useWindowScroll } from 'react-use'

import Link from '@/components/Link'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetContentProps } from '@/components/ui/sheet'

import { studioSignInUrl, studioSignUpUrl } from '@/constants/externalUrls'
import { ReactComponent as DataChainSVG } from '@/static/images/logos/datachain_logotype.svg'
import { cn, isExternalLink } from '@/utils'

import menuItems, { MenuItem } from './menuItems'
import NavDropdownMenu from './NavDropdownMenu'
import NavDropdownMobileMenu from './NavDropdownMobileMenu'
import NavMenuButton from './NavMenuButton'

const NavMenuItem = ({
  menuItem,
  closeMenu,
  mobile = false
}: {
  menuItem: MenuItem
  mobile?: boolean
  closeMenu?: () => void
}) => {
  return (
    <li className="rounded-lg py-1 hover:bg-zinc-900">
      {menuItem.items && menuItem.items.length > 0 ? (
        mobile ? (
          <NavDropdownMobileMenu
            content={
              <ul className="space-y-1 p-1">
                {menuItem.items.map((item, index) => {
                  return (
                    <li key={index}>
                      <Link
                        href={item.link}
                        onClick={closeMenu}
                        className="inline-flex w-full bg-zinc-900 p-2"
                      >
                        {item.icon ? (
                          <div className="grid grid-cols-[auto,1fr] gap-2">
                            <div className="w-10 p-2">{item.icon}</div>
                            <div>
                              <p className="text-sm font-medium">{item.name}</p>
                              <p className="min-w-[200px] text-xs text-gray-500">
                                {item.description}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm font-medium">{item.name}</p>
                        )}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            }
          >
            {menuItem.name}
          </NavDropdownMobileMenu>
        ) : (
          <NavDropdownMenu
            content={
              <ul className="p-1">
                {menuItem.items.map((item, index) => {
                  return (
                    <li key={index}>
                      <Link
                        href={item.link}
                        className="group inline-flex w-full rounded-lg p-2 hover:bg-zinc-200"
                        onClick={closeMenu}
                      >
                        {item.icon ? (
                          <div className="grid grid-cols-[auto,1fr] gap-2">
                            <div className="w-10 p-2">{item.icon}</div>
                            <div>
                              <p className="inline-flex items-center text-sm font-medium">
                                <span className="block">{item.name}</span>
                                {isExternalLink(item.link) && (
                                  <ExternalLink
                                    className="ml-1 text-gray-500 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                                    size={14}
                                    strokeWidth={3}
                                  />
                                )}
                              </p>
                              <p className="min-w-[200px] text-xs text-gray-500">
                                {item.description}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <p className="whitespace-nowrap text-sm font-medium">
                            {item.name}
                          </p>
                        )}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            }
          >
            {menuItem.name}
          </NavDropdownMenu>
        )
      ) : (
        menuItem.link && (
          <Link
            href={menuItem.link}
            onClick={closeMenu}
            className="inline-flex px-3 py-1"
          >
            {menuItem.name}
          </Link>
        )
      )}
    </li>
  )
}

const NavBar = ({ sticky = false }: { sticky?: boolean }) => {
  const { y: scrolledY } = useWindowScroll()

  const [openMenu, setOpenMenu] = useState(false)

  return (
    <div
      className={cn(
        `w-full bg-zinc-950 p-2 md:px-4 lg:px-8`,
        openMenu && `h-screen lg:h-auto`,
        sticky && `sticky top-0 z-50`,
        sticky &&
          scrolledY > 10 &&
          `border-b border-b-slate-50/20 backdrop-blur supports-[backdrop-filter]:bg-zinc-950/40`
      )}
    >
      <div className="mx-auto max-w-screen-2xl">
        <nav className="duration-700 animate-in slide-in-from-top-5">
          <div className="flex justify-between gap-6">
            {/* Left side nav */}
            <div className="flex items-center">
              <Link
                href="/"
                className={cn(
                  `mr-2 rounded-lg p-2 text-3xl hover:bg-zinc-900`,
                  `flex flex-col items-center`,
                  `whitespace-nowrap`
                )}
              >
                <DataChainSVG className="h-8 w-32" />
              </Link>
              <ul className="hidden lg:flex lg:items-center">
                {menuItems.map((menuItem, index) => (
                  <NavMenuItem menuItem={menuItem} key={index} />
                ))}
              </ul>
            </div>
            {/* Right side nav */}
            <ul className="flex items-center gap-2">
              <li className="mr-2 hidden xs:flex">
                <Link href={studioSignUpUrl} showExternalIcon={false}>
                  Sign up
                </Link>
              </li>
              <li>
                <Button size="sm" variant="outline" asChild rounded="full">
                  <Link href={studioSignInUrl} showExternalIcon={false}>
                    Sign in
                  </Link>
                </Button>
              </li>

              <li className="lg:hidden">
                <NavMenuButton open={openMenu} setOpen={setOpenMenu} />
              </li>
            </ul>
          </div>

          <MobileDrawer open={openMenu} setOpen={setOpenMenu} />
        </nav>
      </div>
    </div>
  )
}

const MobileDrawer = ({
  open,
  setOpen,
  side = `right`
}: {
  open: boolean
  setOpen: (open: boolean) => void
  side?: SheetContentProps['side']
}) => {
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side={side}
        className="flex flex-col justify-between overflow-y-auto"
      >
        <ul className="flex flex-col gap-2">
          {menuItems.map((menuItem, index) => {
            return (
              <NavMenuItem
                menuItem={menuItem}
                key={index}
                mobile
                closeMenu={() => setOpen(false)}
              />
            )
          })}
        </ul>
        <div className="flex flex-wrap items-center justify-center gap-4 pb-6 pt-3">
          <div className="mr-2 xs:hidden">
            <Link href={studioSignUpUrl} showExternalIcon={false}>
              Sign up
            </Link>
          </div>
          <div>
            <Button
              className="xs:hidden"
              size="sm"
              variant="outline"
              asChild
              rounded="full"
            >
              <Link href={studioSignInUrl} showExternalIcon={false}>
                Sign in
              </Link>
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default NavBar
